import { RealTimeAction } from '@/_core/fluxstore/types';
import { Activity, StorymapState, Subtask, SubtaskStatusCategory, Task } from '../types';
import { useStorymapStore } from '../useModule';
import { uuid } from '@/_core/utils/uuid';
import { useSendCommand } from '@/_core/cqrs/use';
import { PasteCardFromClipboard, PasteCardFromClipboardResult } from '@/api/storymapCardDetails/commands';
import { getCardById, isActivity, isSubtask, isTask } from '../use';
import { SetActiveCardIdByClick } from './cardSelection/setActiveCardIdByClick';
import { FlashJustDragged } from './moveCard/flashJustDragged';
import orderBy from 'lodash/orderBy';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class PasteCopiedCard extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'PasteCopiedCard';
    public _newCardId: string;
    public _cardKey: string;

    public constructor(public copiedCardId: string, public destinationCardId: string) {
        super(PasteCopiedCard.ACTION_TYPE);
    }
}

const middleware = async (state: StorymapState, action: PasteCopiedCard): Promise<boolean> => {
    action._newCardId = await uuid.create();

    const sourceCard = getCardById(action.copiedCardId);
    const destinationCard = getCardById(action.destinationCardId);

    const cmd = await PasteCardFromClipboard.create(
        action._newCardId,
        action.copiedCardId,
        sourceCard.title,
        destinationCard.order + 1,
        state.id,
        isTask(destinationCard) ? destinationCard.activityId : null,
        isSubtask(destinationCard) ? destinationCard.taskId : null,
        isSubtask(destinationCard) ? destinationCard.releaseId : null
    );

    return useSendCommand(action, {
        createCommand: () => cmd,
        onSuccess: (res: PasteCardFromClipboardResult) => {
            action._cardKey = res.CardKey;
        },
    });
};

const reducer = (state: StorymapState, action: PasteCopiedCard): void => {
    const sourceCard = getCardById(action.copiedCardId);
    const destinationCard = getCardById(action.destinationCardId);

    let newCard: Activity | Task | Subtask = null;

    if (isActivity(destinationCard)) {
        state.activities.filter((x) => x.order > destinationCard.order).forEach((x) => x.order++);

        const newActivity: Activity = {
            id: action._newCardId,
            title: sourceCard.title,
            color: sourceCard.color,
            annotationIds: [...sourceCard.annotationIds],
            personaIds: [...sourceCard.personaIds],
            assigneeUserId: null,
            description: sourceCard.description,
            hasAttachment: sourceCard.hasAttachment,
            hasDescription: sourceCard.hasDescription,
            hasComment: sourceCard.hasComment,
            hasFeedback: sourceCard.hasFeedback,
            order: destinationCard.order + 1,
            createdAt: new Date(),
            createdByUserId: initAppData.currentUser.currentUserShortId,
            key: action._cardKey,
            isCollapsed: false,

            isActive: false,
            activeUsers: [],
            isCopied: false,
            isCut: false,
            isLinkedToIntegrationTool: false,
            isMultiselected: false,
            isNew: false,
            isSaving: false,
            isTitleEditing: false,
            linkedUrl: null,
            linkedItemKey: null,
            linkedItemTypeId: null,
            linkedItemTypeName: null,
            lastUpdatedAt: null,
            lastUpdatedByUserId: null,
        };

        state.activities.push(newActivity);
        newCard = newActivity;
    }
    if (isTask(destinationCard)) {
        state.tasks.filter((x) => x.activityId == destinationCard.activityId && x.order > destinationCard.order).forEach((x) => x.order++);

        const newTask: Task = {
            id: action._newCardId,
            title: sourceCard.title,
            color: sourceCard.color,
            annotationIds: [...sourceCard.annotationIds],
            personaIds: [...sourceCard.personaIds],
            assigneeUserId: null,
            description: sourceCard.description,
            hasAttachment: sourceCard.hasAttachment,
            hasDescription: sourceCard.hasDescription,
            hasComment: sourceCard.hasComment,
            hasFeedback: sourceCard.hasFeedback,
            order: destinationCard.order + 1,
            activityId: destinationCard.activityId,
            createdAt: new Date(),
            createdByUserId: initAppData.currentUser.currentUserShortId,
            key: action._cardKey,

            isActive: false,
            activeUsers: [],
            isCopied: false,
            isCut: false,
            isLinkedToIntegrationTool: false,
            isMultiselected: false,
            isNew: false,
            isSaving: false,
            isTitleEditing: false,
            linkedUrl: null,
            linkedItemKey: null,
            linkedItemTypeId: null,
            linkedItemTypeName: null,
            lastUpdatedAt: null,
            lastUpdatedByUserId: null,
        };

        state.tasks.push(newTask);
        newCard = newTask;
    }
    if (isSubtask(destinationCard)) {
        state.subtasks
            .filter(
                (x) => x.taskId == destinationCard.taskId && x.releaseId == destinationCard.releaseId && x.order > destinationCard.order
            )
            .forEach((x) => x.order++);
        const defaultStatus = orderBy(
            state.subtaskStatuses.filter((x) => x.category === SubtaskStatusCategory.Todo),
            (x) => x.order
        )[0].key;

        const newSubtask: Subtask = {
            id: action._newCardId,
            title: sourceCard.title,
            color: sourceCard.color,
            annotationIds: [...sourceCard.annotationIds],
            personaIds: [...sourceCard.personaIds],
            assigneeUserId: null,
            description: sourceCard.description,
            hasAttachment: sourceCard.hasAttachment,
            hasDescription: sourceCard.hasDescription,
            hasComment: sourceCard.hasComment,
            hasFeedback: sourceCard.hasFeedback,
            order: destinationCard.order + 1,
            status: (sourceCard as Subtask).status || defaultStatus,
            estimation: (sourceCard as Subtask).estimation,
            activityId: destinationCard.activityId,
            taskId: destinationCard.taskId,
            releaseId: destinationCard.releaseId,
            createdAt: new Date(),
            createdByUserId: initAppData.currentUser.currentUserShortId,
            key: action._cardKey,

            isActive: false,
            activeUsers: [],
            isCopied: false,
            isCut: false,
            isLinkedToIntegrationTool: false,
            isMultiselected: false,
            isNew: false,
            isSaving: false,
            isTitleEditing: false,
            linkedUrl: null,
            linkedItemKey: null,
            linkedItemTypeId: null,
            linkedItemTypeName: null,
            basicPrioValue: null,
            businessValue: null,
            effort: null,
            kanoPrioValue: null,
            moscowPrioValue: null,
            releaseViewOrder: null,
            lastUpdatedAt: null,
            lastUpdatedByUserId: null,
        };

        if (isSubtask(sourceCard)) {
            newSubtask.basicPrioValue = sourceCard.basicPrioValue;
            newSubtask.businessValue = sourceCard.businessValue;
            newSubtask.effort = sourceCard.effort;
            newSubtask.kanoPrioValue = sourceCard.kanoPrioValue;
            newSubtask.moscowPrioValue = sourceCard.moscowPrioValue;
        }

        state.subtasks.push(newSubtask);
        newCard = newSubtask;
    }

    if (!action.isDispatchedByRealTimeCallback) {
        dispatch(new SetActiveCardIdByClick(newCard.id));
        dispatch(new FlashJustDragged());
    }
};

//@ts-ignore
registerMiddleware(PasteCopiedCard.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(PasteCopiedCard.ACTION_TYPE, reducer);
